import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TaskRoute } from "tm-shared/types";

import { CommentModal } from "@/components/Modals/CommentModal";
import CreateCustomTaskModal from "@/components/Modals/CustomTask/CreateCustomTaskModal";
import CustomTaskAddPhotosModal from "@/components/Modals/CustomTask/CustomTaskAddPhotosModal";
import ExportFileModal from "@/components/Modals/ExportFile";
import KpiProgressModal from "@/components/Modals/KpiProgress";
import MessageModal from "@/components/Modals/Message";
import PromoModal from "@/components/Modals/Promo";
import { StartVisitModal } from "@/components/Modals/StartVisitModal";
import { StartVisitModalWarning } from "@/components/Modals/StartVisitModalWarning";
import StateModal from "@/components/Modals/State";
import { TaskInfoModal } from "@/components/Modals/TaskInfoModal";
import UnscheduledMarketCompletionModal from "@/components/Modals/UncheduledMarketCompletion";
import { UncompletedCheckoutsModal } from "@/components/Modals/UncompletedCheckouts";
import { UncompletedTaskModal } from "@/components/Modals/UncompletedTask";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { removeModalFromQueue } from "@/store/slices/ui";
import { ModalState, ModalType } from "@/types/modals";

// Пріоритети модалок (чим більше - тим вищий пріоритет)
const MODAL_PRIORITIES = {
    [ModalType.KpiProgress]: 60,
    [ModalType.Promos]: 50,
    [ModalType.UnscheduledMarketCompletion]: 40,
    [ModalType.State]: 40,
    [ModalType.Messages]: 30,
    [ModalType.Exports]: 20,
    [ModalType.UncompletedTasks]: 10,
    [ModalType.UncompletedCheckouts]: 0,
    [ModalType.StartVisit]: 0,
    [ModalType.StartVisitWarning]: 0,
} as const;

export const ModalsContainer: React.FC = () => {
    const dispatch = useAppDispatch();
    const { isInitialDataLoading } = useAppSelector((state) => state.offline);
    const { isAuthenticated } = useAppSelector((state) => state.auth);
    const { modalsQueueList } = useAppSelector((state) => state.ui);

    // Один стейт для поточної модалки
    const [currentModal, setCurrentModal] = useState<ModalState | null>(null);

    // Сортуємо модалки за пріоритетом (від більшого до меншого)
    const sortedModals = useMemo(
        () =>
            [...modalsQueueList].sort((a, b) => {
                const priorityA = MODAL_PRIORITIES[a.type] ?? 0;
                const priorityB = MODAL_PRIORITIES[b.type] ?? 0;
                return priorityB - priorityA;
            }),
        [modalsQueueList],
    );

    useEffect(() => {
        if (sortedModals.length === 0) {
            setCurrentModal(null);
            return;
        }

        const reminderModals = [
            ModalType.UncompletedTasks,
            ModalType.UncompletedCheckouts,
        ];

        const isOnTaskRoute = Object.values(TaskRoute).some((route) =>
            window.location.pathname.includes(route),
        );

        if (isOnTaskRoute) {
            const filteredModal = sortedModals.find(
                (m) => !reminderModals.includes(m.type),
            );
            setCurrentModal(filteredModal || null);
            return;
        }

        setCurrentModal(sortedModals[0]);
    }, [sortedModals]);

    const handleCloseModal = useCallback(
        (modalKey: string) => {
            setCurrentModal(null);
            dispatch(removeModalFromQueue(modalKey));
        },
        [dispatch],
    );

    if (isInitialDataLoading || !isAuthenticated || !currentModal) {
        return null;
    }

    switch (currentModal.type) {
        case ModalType.Promos:
            return (
                <PromoModal
                    key={currentModal.id}
                    payload={currentModal.payload}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                />
            );

        case ModalType.Messages:
            return (
                <MessageModal
                    key={currentModal.id}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );

        case ModalType.KpiProgress:
            return (
                <KpiProgressModal
                    key={currentModal.id}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );
        case ModalType.UnscheduledMarketCompletion:
            return (
                <UnscheduledMarketCompletionModal
                    key={currentModal.id}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );

        case ModalType.Exports:
            return (
                <ExportFileModal
                    key={currentModal.id}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );

        case ModalType.UncompletedCheckouts:
            return (
                <UncompletedCheckoutsModal
                    key={currentModal.id}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                />
            );

        case ModalType.UncompletedTasks:
            return (
                <UncompletedTaskModal
                    key={currentModal.id}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                />
            );

        case ModalType.State:
            return (
                <StateModal
                    key={currentModal.id}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );

        case ModalType.StartVisit:
            return (
                <StartVisitModal
                    key={currentModal.id}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );

        case ModalType.StartVisitWarning:
            return (
                <StartVisitModalWarning
                    key={currentModal.id}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );

        case ModalType.Comment:
            return (
                <CommentModal
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );
        case ModalType.TaskInfo:
            return (
                <TaskInfoModal
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );
        case ModalType.CustomTaskAddPhotos:
            return (
                <CustomTaskAddPhotosModal
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );
        case ModalType.CreateCustomTask:
            return (
                <CreateCustomTaskModal
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );

        default:
            return null;
    }
};
